import { Suspense, type CSSProperties } from 'react';
import { Link, Outlet } from 'react-router-dom';

import { Icon, Spinner, type IconName } from '@/components/ui';
import { useEdgeColor } from '@/lib/hooks';

import styles from './PublicLayout.module.css';

const MAIN_CONTENT_ID = 'conteudo-publico';

const BENEFITS: readonly { icon: IconName; title: string; text: string }[] = [
  {
    icon: 'tag',
    title: 'Catálogo e preços da sua tabela',
    text: 'Só aparece o que foi negociado para a sua empresa, com o preço vigente.',
  },
  {
    icon: 'clock',
    title: 'Pedido editável por 1 hora',
    text: 'Depois de enviar, ainda dá para ajustar; ele fecha sozinho e o boleto chega por e-mail.',
  },
  {
    icon: 'truck',
    title: 'Entrega acompanhada',
    text: 'Do fechamento à entrega, cada etapa aparece na página do pedido.',
  },
];

/**
 * The public screens — signing in and getting back in (Figma AC1–AC5, ES6A): the brand's red
 * band on top, as the storefront has, the presentation on the left and the screen's own card on
 * the right, a quiet footer below. It replaces the red story panel with the map.
 *
 * Motion (AC1): the band stays still; the presentation cascades 50 ms (benefits 60 ms) and the
 * card rises 12px (320 ms, out-strong), with the shell's route transition around it.
 */
export function PublicLayout() {
  useEdgeColor();

  return (
    <div className={styles.layout} data-public-layout="">
      <header className={styles.header}>
        <Link to="/" className={styles.brand}>
          <span className={styles.mark} aria-hidden="true">
            In
          </span>
          Instoque
        </Link>
        <span className={styles.tagline}>Compras de food service com a Sapore</span>
      </header>

      <main className={styles.main} id={MAIN_CONTENT_ID} tabIndex={-1}>
        <section className={styles.story} aria-label="Sobre a Instoque">
          <p className={styles.eyebrow} style={{ '--i': 0 } as CSSProperties}>
            <Icon name="building" size={16} />
            Compras de food service · Sapore
          </p>
          <p className={styles.storyTitle} style={{ '--i': 1 } as CSSProperties}>
            O pedido da semana, em poucos cliques.
          </p>
          <p className={styles.storyText} style={{ '--i': 2 } as CSSProperties}>
            O catálogo negociado da sua empresa, o pedido aberto por uma hora e o boleto depois do
            fechamento. Tudo no mesmo lugar.
          </p>
          <ul className={styles.benefits}>
            {BENEFITS.map((benefit, index) => (
              <li
                key={benefit.title}
                className={styles.benefit}
                style={{ '--j': index } as CSSProperties}
              >
                <span className={styles.benefitIcon} aria-hidden="true">
                  <Icon name={benefit.icon} size={20} />
                </span>
                <span>
                  <strong>{benefit.title}</strong>
                  <span>{benefit.text}</span>
                </span>
              </li>
            ))}
          </ul>
          <p className={styles.lgpd}>
            <Icon name="shield" size={16} />
            Seus dados são tratados conforme a LGPD.
          </p>
        </section>

        <div className={styles.content}>
          <Suspense fallback={<Spinner size="lg" label="Carregando…" />}>
            <Outlet />
          </Suspense>
        </div>
      </main>

      <footer className={styles.footer}>
        <span>© {new Date().getFullYear()} Instoque · compras de food service com a Sapore</span>
        <span>
          Instoque · versão <code>{import.meta.env.VITE_APP_VERSION}</code>
        </span>
      </footer>
    </div>
  );
}
